import { IonIcon } from '@ionic/react'
import Check from '@mui/icons-material/Check'
import PriorityHigh from '@mui/icons-material/PriorityHigh'
import {
	CircularProgress,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	TextField,
	TextFieldProps,
} from '@mui/material'
import { SynerallAccountingPoint } from '@vae/server/dist/modules/ExternalAPIs/Synerall/synerallTypes'
import { home } from 'ionicons/icons'
import React, { useContext, useMemo, useState } from 'react'
import { Control, Controller, UseFormReturn, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/actions/Button'
import ModeSwitch from '../../../components/ModeSwitch'
import { AppContext } from '../../../contexts/AppContext'
import { trpc } from '../../../modules/trpc'
import { MoveOutFormFields } from './MoveOutWizard'

interface Props {
	nextSlide: () => void
	formState: UseFormReturn<MoveOutFormFields>
}

interface InfoFieldProps {
	control: Control<MoveOutFormFields>
	title: string
	success: boolean
	onBlur?: (nogsrn?: boolean) => Promise<void>
	watch: UseFormWatch<MoveOutFormFields>
	prefix: 'contract.' | 'contract.invoice'
}

const ObjectInfoFields: React.FC<InfoFieldProps> = ({
	control,
	title,
	success,
	onBlur,
	watch,
	prefix,
}) => {
	const { t } = useTranslation('slide_meteringPointInformationForm')

	const sharedFieldProps: TextFieldProps = {
		focused: success,
		onBlur: () => onBlur?.(),
	}

	const isMainAddress = prefix.length < 1

	return (
		<div className='metering-point-address-information'>
			<h3 className='text-text-dark dark:text-white'>{title}</h3>
			<div className='form-row'>
				<Controller
					control={control}
					name={`${prefix}streetAddress`}
					defaultValue=''
					rules={{
						required: isMainAddress,
					}}
					render={({ field }) => (
						<TextField
							{...field}
							{...sharedFieldProps}
							label={t('streetAddress')}
							required
							value={field.value ?? ''}
						/>
					)}
				/>

				<Controller
					control={control}
					name={`${prefix}houseNumber`}
					defaultValue=''
					rules={{
						required: isMainAddress,
					}}
					render={({ field }) => (
						<TextField
							{...field}
							{...sharedFieldProps}
							label={t('number')}
							required
							value={field.value ?? ''}
						/>
					)}
				/>

				<Controller
					control={control}
					name={`${prefix}stairwellIdentification`}
					defaultValue=''
					rules={{
						required: Boolean(watch(`${prefix}apartment`)) && isMainAddress,
					}}
					render={({ field }) => (
						<TextField
							{...field}
							{...sharedFieldProps}
							label={t('stairwellOrApartment')}
							required={Boolean(watch(`${prefix}apartment`))}
							value={field.value ?? ''}
						/>
					)}
				/>

				<Controller
					control={control}
					name={`${prefix}apartment`}
					defaultValue=''
					render={({ field }) => (
						<TextField
							{...field}
							{...sharedFieldProps}
							label={t('apartment')}
							value={field.value ?? ''}
						/>
					)}
				/>
			</div>
			<div className='form-row'>
				<Controller
					control={control}
					name={`${prefix}zipCode`}
					defaultValue=''
					rules={{
						required: isMainAddress,
					}}
					render={({ field }) => (
						<TextField
							{...field}
							{...sharedFieldProps}
							label={t('zipcode')}
							required
							focused={success}
							onBlur={() => onBlur?.()}
							value={field.value ?? ''}
						/>
					)}
				/>
			</div>
		</div>
	)
}

const isNotEmpty = (value: string | undefined): boolean => {
	if (value === undefined) return false
	if (value === null) return false
	if (value === '') return false

	return true
}

const MeteringPointInformationForm: React.FC<Props> = ({ formState }) => {
	const { t } = useTranslation('slide_meteringPointInformationForm')

	const [mode, setMode] = useState<'address' | 'gsrn'>('address')
	const [checkingAccountingPoint, setCheckingAccountingPoint] = useState(false)
	const [accountingPointFound, setAccountingPointFound] = useState<boolean | null>(null)
	const [selectedAccountingPoint, setselectedAccountingPoint] =
		useState<SynerallAccountingPoint | null>(null)
	const [acOptions, setAcOptions] = useState<SynerallAccountingPoint[]>([])
	const {
		control,
		formState: { dirtyFields },
		getValues,
		setValue,
		watch,
	} = formState

	const context = trpc.useContext()

	const values = getValues()
	const areRequiredFilled = useMemo(() => {
		return (
			(isNotEmpty(values.contract?.streetAddress ?? '') &&
				isNotEmpty(values.contract?.houseNumber ?? '') &&
				isNotEmpty(values.contract?.zipCode ?? '')) ||
			isNotEmpty(values.contract?.gsrn ?? '')
		)
	}, [values])

	const selectAccountingPoint = (accountingPoint: SynerallAccountingPoint) => {
		setselectedAccountingPoint(accountingPoint)
		setValue('contract.gsrn', accountingPoint.Gsrn)
		setValue('contract.streetAddress', accountingPoint.Address.Street, { shouldDirty: true })
		setValue('contract.houseNumber', accountingPoint.Address.House)
		setValue('contract.stairwellIdentification', accountingPoint.Address.Stairwell ?? null)
		setValue('contract.apartment', accountingPoint.Address.Apartment ?? null)
		setValue('contract.zipCode', accountingPoint.Address.ZipCode)
		setValue('contract.city', accountingPoint.Address.City)
		const currentContractGsrns = getValues('currentContractGsrns') ?? []
		if (currentContractGsrns.some(x => x === accountingPoint.Gsrn)) {
			setValue('isExistingCustomer', true)
		} else {
			setValue('isExistingCustomer', false)
		}

		setAccountingPointFound(true)
	}

	const handleObjectCheck = async (manual?: boolean) => {
		if (!areRequiredFilled || (accountingPointFound && !manual)) return
		const values = getValues()

		setCheckingAccountingPoint(true)
		setAccountingPointFound(null)
		setselectedAccountingPoint(null)

		await context.synerall.accountingPointSearch.invalidate()
		const result = await context.synerall.accountingPointSearch.fetch(
			mode === 'address'
				? {
						Apartment: values.contract.apartment ?? '',
						StreetName: values.contract.streetAddress ?? '',
						BuildingNumber: values.contract.houseNumber ?? '',
						PostalCode: values.contract.zipCode ?? '',
						StairwellIdentification: values.contract.stairwellIdentification ?? '',
					}
				: {
						Gsrn: values.contract.gsrn ?? '',
					},
			{ initialData: { AccountingPoints: [], MoreThen50Results: false } }
		)

		setCheckingAccountingPoint(false)

		if (!result) {
			setAccountingPointFound(false)
			return
		}

		const accountingPoints = result.AccountingPoints

		if ((accountingPoints?.length ?? 0) === 1 && mode === 'gsrn') {
			setAccountingPointFound(true)
			return selectAccountingPoint(accountingPoints[0])
		}

		if (accountingPoints.length === 1) {
			if (
				(accountingPoints[0].Address.Stairwell ?? '') !==
				(values.contract.stairwellIdentification ?? '')
			) {
				console.warn('Stairwell does not match the provided value.')
				setAccountingPointFound(false)
				return
			}
			selectAccountingPoint(accountingPoints[0])
			return
		}

		if (accountingPoints.length > 1) {
			setAcOptions(accountingPoints)
			return
		}

		setAccountingPointFound(false)
	}

	const { isDarkMode } = useContext(AppContext)

	return (
		<div id='metering-point-information-form'>
			{!(acOptions.length > 1) && (
				<div className='metering-point-information'>
					<ModeSwitch<'address' | 'gsrn'>
						options={[
							{ mode: 'address', label: t('addressDetails') },
							{ mode: 'gsrn', label: t('gsrnDescription') },
						]}
						currentMode={mode}
						onModeChange={setMode}
					/>

					{mode === 'gsrn' ? (
						<Controller
							control={control}
							name={`contract.gsrn`}
							defaultValue=''
							rules={{
								required: true,
								maxLength: 18,
								minLength: 18,
							}}
							render={({ field }) => (
								<TextField
									{...field}
									className='gsrn-input'
									label={t('gsrmLabel')}
									required
									focused={Boolean(accountingPointFound) && (dirtyFields.contract?.gsrn ?? false)}
									onBlur={() => handleObjectCheck()}
									type='number'
									onFocus={() => {
										if (selectedAccountingPoint) setselectedAccountingPoint(null)
									}}
									InputProps={{
										endAdornment: Boolean(accountingPointFound) &&
											(dirtyFields.contract?.gsrn ?? false) && (
												<InputAdornment position='end'>
													<Check />
												</InputAdornment>
											),
									}}
								/>
							)}
						/>
					) : (
						<ObjectInfoFields
							title={t('locationAddress')}
							prefix='contract.'
							control={control}
							success={
								Boolean(selectedAccountingPoint) && (dirtyFields.contract?.streetAddress ?? false)
							}
							onBlur={handleObjectCheck}
							watch={watch}
						/>
					)}

					{accountingPointFound !== null && !selectedAccountingPoint && (
						<Button
							onClick={() => handleObjectCheck(true)}
							color='secondary'
							disabled={!areRequiredFilled}
						>
							{t('refetch')}
						</Button>
					)}

					{checkingAccountingPoint && (
						<>
							<CircularProgress />
							<span>{t('checkingLocation')}</span>
						</>
					)}

					{accountingPointFound === false && mode === 'address' && (
						<div className='accounting-point-not-found'>
							<PriorityHigh />
							{t('noLocationFound')}
						</div>
					)}
					{accountingPointFound === false && mode === 'gsrn' && (
						<div className='accounting-point-not-found'>
							<PriorityHigh />
							{t('noLocationFoundGsrn')}
						</div>
					)}

					{selectedAccountingPoint && (
						<div className='accounting-point-found'>
							<h3>
								<Check />
								{t('locationFound')}
							</h3>
							<div className='values'>
								<div className='row'>Gsrn: {selectedAccountingPoint?.Gsrn}</div>
								<div className='row'>
									{`${t('address')} ${selectedAccountingPoint?.Address.StreetAddress}`}
								</div>
								<div className='row'>
									{t('zipCode')} {selectedAccountingPoint?.Address.ZipCode}
								</div>
								<div className='row'>
									{t('city')} {selectedAccountingPoint?.Address.City}
								</div>
							</div>
						</div>
					)}
				</div>
			)}

			{acOptions.length > 1 && (
				<div className='object-chooser'>
					<h3>{t('searchResultsCount', { count: acOptions.length })}</h3>
					<List className='ac-options-list'>
						{acOptions.map(element => (
							<ListItem key={element.Gsrn}>
								<ListItemButton
									className='!rounded-lg'
									sx={{ border: `1px solid ${isDarkMode ? '#3cb49f' : '#52ae32'}` }}
									onClick={() => {
										selectAccountingPoint(element)
										setAcOptions([])
									}}
								>
									<ListItemIcon>
										<IonIcon icon={home} className='text-xl' />
									</ListItemIcon>
									<ListItemText>
										{element.Address.StreetAddress}, {element.Address.ZipCode}{' '}
										{element.Address.City}
									</ListItemText>
								</ListItemButton>
							</ListItem>
						))}
					</List>
				</div>
			)}
		</div>
	)
}

export default MeteringPointInformationForm
